import React, { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import { 
  Settings, 
  RefreshCw, 
  CheckCircle, 
  AlertTriangle,
  BarChart3,
  Users,
  Calendar
} from 'lucide-react'
import { Button } from '../ui/button'
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card'
import LoadingSpinner from '../common/LoadingSpinner'

const AdminTools = () => {
  const [updateResults, setUpdateResults] = useState(null)
  const queryClient = useQueryClient()

  // Get current quota status
  const { data: quotaStatus, isLoading: loadingStatus, refetch } = useQuery(
    'admin-quota-status',
    () => apiService.get('/admin-tools/quota-status').then(res => res.data),
    {
      retry: 1
    }
  )

  // Update client quotas mutation
  const updateQuotasMutation = useMutation(
    () => apiService.post('/admin-tools/update-client-quotas'),
    {
      onSuccess: (response) => {
        const data = response.data
        setUpdateResults(data)
        toast.success(`Updated ${data.summary.updated_clients} clients successfully!`)
        refetch() // Refresh the status
        queryClient.invalidateQueries('client')
        queryClient.invalidateQueries('clients')
      },
      onError: (error) => {
        console.error('Update error:', error)
        toast.error(error.response?.data?.message || 'Failed to update quotas')
      }
    }
  )

  const handleUpdateQuotas = () => {
    if (confirm('Are you sure you want to update all client quotas based on their subscription plans? This will affect all clients.')) {
      updateQuotasMutation.mutate()
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-neutral-900 dark:text-neutral-50">
          Admin Tools
        </h1>
        <p className="text-neutral-600 dark:text-neutral-400">
          System administration and maintenance tools
        </p>
      </div>

      {/* Quota Management */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Settings className="w-5 h-5 text-accent-600 dark:text-accent-400" />
            <span>Client Quota Management</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {loadingStatus ? (
            <LoadingSpinner size="sm" text="Loading quota status..." />
          ) : quotaStatus ? (
            <div className="space-y-4">
              {/* Summary Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-neutral-50 dark:bg-neutral-800 rounded-lg">
                  <Users className="w-6 h-6 text-neutral-400 mx-auto mb-1" />
                  <div className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                    {quotaStatus.summary.total_clients}
                  </div>
                  <div className="text-sm text-neutral-500 dark:text-neutral-400">
                    Total Clients
                  </div>
                </div>
                
                <div className="text-center p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                  <AlertTriangle className="w-6 h-6 text-yellow-500 mx-auto mb-1" />
                  <div className="text-2xl font-bold text-yellow-900 dark:text-yellow-100">
                    {quotaStatus.summary.needs_update}
                  </div>
                  <div className="text-sm text-yellow-600 dark:text-yellow-400">
                    Need Updates
                  </div>
                </div>

                <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <BarChart3 className="w-6 h-6 text-blue-500 mx-auto mb-1" />
                  <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                    {quotaStatus.summary.plans.basic}B / {quotaStatus.summary.plans.pro}P / {quotaStatus.summary.plans.enterprise}E
                  </div>
                  <div className="text-sm text-blue-600 dark:text-blue-400">
                    Plans (B/P/E)
                  </div>
                </div>

                <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-green-500 mx-auto mb-1" />
                  <div className="text-2xl font-bold text-green-900 dark:text-green-100">
                    {quotaStatus.summary.total_clients - quotaStatus.summary.needs_update}
                  </div>
                  <div className="text-sm text-green-600 dark:text-green-400">
                    Up to Date
                  </div>
                </div>
              </div>

              {/* Action Button */}
              <div className="flex justify-center pt-4">
                <Button
                  onClick={handleUpdateQuotas}
                  disabled={updateQuotasMutation.isLoading || quotaStatus.summary.needs_update === 0}
                  className="bg-accent-600 hover:bg-accent-700"
                >
                  {updateQuotasMutation.isLoading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Settings className="w-4 h-4 mr-2" />
                  )}
                  {quotaStatus.summary.needs_update === 0 
                    ? 'All Quotas Up to Date' 
                    : `Update ${quotaStatus.summary.needs_update} Client Quotas`
                  }
                </Button>
              </div>

              {/* Client List */}
              {quotaStatus.clients && (
                <div className="mt-6">
                  <h3 className="text-lg font-medium text-neutral-900 dark:text-neutral-100 mb-4">
                    Client Quota Status
                  </h3>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {quotaStatus.clients.map((client) => (
                      <div 
                        key={client.id}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          client.needs_update 
                            ? 'border-yellow-200 bg-yellow-50 dark:border-yellow-700 dark:bg-yellow-900/20'
                            : 'border-green-200 bg-green-50 dark:border-green-700 dark:bg-green-900/20'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          {client.needs_update ? (
                            <AlertTriangle className="w-4 h-4 text-yellow-500" />
                          ) : (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          )}
                          <div>
                            <div className="font-medium text-neutral-900 dark:text-neutral-100">
                              {client.company_name}
                            </div>
                            <div className="text-sm text-neutral-500 dark:text-neutral-400">
                              {client.plan.charAt(0).toUpperCase() + client.plan.slice(1)} Plan
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-medium text-neutral-900 dark:text-neutral-100">
                            {client.usage_display}
                          </div>
                          {client.needs_update && (
                            <div className="text-sm text-yellow-600 dark:text-yellow-400">
                              Should be: {client.used}/{client.expected_quota}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <AlertTriangle className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
              <p className="text-neutral-500 dark:text-neutral-400">
                Failed to load quota status
              </p>
              <Button onClick={() => refetch()} className="mt-2">
                Retry
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Update Results */}
      {updateResults && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span>Update Results</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                    {updateResults.summary.total_clients}
                  </div>
                  <div className="text-sm text-neutral-500 dark:text-neutral-400">
                    Total Clients
                  </div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-600">
                    {updateResults.summary.updated_clients}
                  </div>
                  <div className="text-sm text-neutral-500 dark:text-neutral-400">
                    Updated
                  </div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-neutral-600">
                    {updateResults.summary.already_correct}
                  </div>
                  <div className="text-sm text-neutral-500 dark:text-neutral-400">
                    Already Correct
                  </div>
                </div>
              </div>

              <div className="text-center">
                <Button 
                  onClick={() => setUpdateResults(null)}
                  variant="outline"
                >
                  Dismiss Results
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

export default AdminTools